import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { EyeOff, Pencil, Plus, Star } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth } from "@/context/AuthContext";
import { api, eur, img, LINES } from "@/lib/api";
import { ProductEditor } from "@/components/admin/ProductEditor";
import { AdminBlog } from "@/components/admin/AdminBlog";
import { AdminCompany, AdminGuide, AdminOrders, AdminSettings } from "@/components/admin/AdminSections";
import { AdminGear } from "@/components/admin/AdminGear";
import { AdminCompetitions, AdminListings } from "@/components/admin/AdminCommunity";
import { AdminAdoption } from "@/components/admin/AdminAdoption";

const Stats = () => {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/admin/stats").then((r) => setS(r.data)); }, []);
  if (!s) return null;
  const items = [["Pedidos pagados", s.orders_paid, "stat-orders"], ["Facturación", eur(s.revenue), "stat-revenue"], ["Pendientes de envío", s.pending_shipment, "stat-pending"], ["Clientes", s.customers, "stat-customers"]];
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-8">
      {items.map(([l, v, id]) => (
        <div key={id} className="bg-[#0B1D3A] text-white rounded p-5"><p className="text-xs uppercase tracking-wider text-blue-200 font-bold">{l}</p><p className="font-display font-black text-3xl mt-1" data-testid={id}>{v}</p></div>
      ))}
    </div>
  );
};

const Products = ({ products, reload }) => {
  const [editing, setEditing] = useState(undefined);
  return (
    <div data-testid="admin-products">
      <button onClick={() => setEditing(null)} data-testid="admin-new-product" className="mb-4 flex items-center gap-2 bg-[#DC2626] hover:bg-[#B91C1C] text-white font-display font-bold px-5 py-2.5 rounded-full"><Plus size={17} /> Nuevo producto</button>
      <div className="overflow-x-auto border border-slate-200 rounded">
        <table className="w-full text-sm">
          <thead className="bg-[#EFF6FF] text-[#0B1D3A] text-left"><tr><th className="p-3">Producto</th><th className="p-3">Línea</th><th className="p-3">Formatos · precio</th><th className="p-3"></th></tr></thead>
          <tbody>
            {products.map((p) => (
              <tr key={p.slug} className="border-t border-slate-100" data-testid={`admin-product-row-${p.slug}`}>
                <td className="p-3"><div className="flex items-center gap-3"><img src={img(p.images?.[0])} alt="" className="w-11 h-11 object-contain bg-slate-50 rounded" />
                  <div><p className="font-semibold">{p.name}</p><p className="flex gap-2 text-xs text-slate-500">{p.featured && <span className="flex items-center gap-0.5 text-[#DC2626]"><Star size={11} /> Destacado</span>}{p.hidden && <span className="flex items-center gap-0.5"><EyeOff size={11} /> Oculto</span>}</p></div></div></td>
                <td className="p-3 whitespace-nowrap">{LINES[p.line]}</td>
                <td className="p-3">{p.formats.map((f) => <div key={f.id}>{f.label} · {eur(f.price)}</div>)}</td>
                <td className="p-3"><button onClick={() => setEditing(p)} data-testid={`admin-edit-${p.slug}`} className="p-2 hover:text-[#DC2626]" aria-label="Editar"><Pencil size={16} /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {editing !== undefined && <ProductEditor key={editing?.slug || "new"} product={editing} open onClose={() => setEditing(undefined)} onSaved={() => { setEditing(undefined); reload(); }} />}
    </div>
  );
};

export default function Admin() {
  const { user } = useAuth();
  const [products, setProducts] = useState([]);
  const reload = () => api.get("/admin/products").then((r) => setProducts(r.data));
  useEffect(() => { if (user?.role === "admin") reload(); }, [user]);

  if (user === null) return <div className="py-32 text-center text-slate-500">Cargando…</div>;
  if (!user) return <Navigate to="/login?next=/admin" replace />;
  if (user.role !== "admin") return <div className="py-32 text-center" data-testid="admin-forbidden">Acceso solo para el equipo FAUNNY.</div>;

  return (
    <div className="max-w-7xl mx-auto px-4 py-10" data-testid="admin-page">
      <h1 className="font-display font-black text-4xl sm:text-5xl text-[#0B1D3A] mb-6">Panel FAUNNY</h1>
      <Stats />
      <Tabs defaultValue="orders">
        <TabsList className="bg-[#EFF6FF] flex-wrap h-auto">
          <TabsTrigger value="orders" data-testid="admin-tab-orders">Pedidos</TabsTrigger>
          <TabsTrigger value="products" data-testid="admin-tab-products">Productos</TabsTrigger>
          <TabsTrigger value="blog" data-testid="admin-tab-blog">Blog</TabsTrigger>
          <TabsTrigger value="guide" data-testid="admin-tab-guide">Guía de salud</TabsTrigger>
          <TabsTrigger value="settings" data-testid="admin-tab-settings">Envío y descuentos</TabsTrigger>
          <TabsTrigger value="company" data-testid="admin-tab-company">Datos legales</TabsTrigger>
          <TabsTrigger value="gear" data-testid="admin-tab-gear">FAUNNY Gear</TabsTrigger>
          <TabsTrigger value="concursos" data-testid="admin-tab-competitions">Concursos</TabsTrigger>
          <TabsTrigger value="mercado" data-testid="admin-tab-market">Mercado</TabsTrigger>
          <TabsTrigger value="adopcion" data-testid="admin-tab-adoption">Adopción</TabsTrigger>
        </TabsList>
        <TabsContent value="orders" className="pt-6"><AdminOrders /></TabsContent>
        <TabsContent value="products" className="pt-6"><Products products={products} reload={reload} /></TabsContent>
        <TabsContent value="blog" className="pt-6"><AdminBlog slugs={products.map((p) => p.slug)} /></TabsContent>
        <TabsContent value="guide" className="pt-6"><AdminGuide slugs={products.map((p) => p.slug)} /></TabsContent>
        <TabsContent value="settings" className="pt-6"><AdminSettings /></TabsContent>
        <TabsContent value="company" className="pt-6"><AdminCompany /></TabsContent>
        <TabsContent value="gear" className="pt-6"><AdminGear /></TabsContent>
        <TabsContent value="concursos" className="pt-6"><AdminCompetitions /></TabsContent>
        <TabsContent value="mercado" className="pt-6"><AdminListings /></TabsContent>
        <TabsContent value="adopcion" className="pt-6"><AdminAdoption /></TabsContent>
      </Tabs>
    </div>
  );
}
